import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, CalendarIcon, Upload, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const DocumentFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useTranslation();
  const { settings } = useSettings();
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formConfigs, setFormConfigs] = useState([]);
  const [approvalConfigs, setApprovalConfigs] = useState([]);
  const [selectedFormConfig, setSelectedFormConfig] = useState(null);
  const [selectedApprovalConfig, setSelectedApprovalConfig] = useState('');
  const [formData, setFormData] = useState({});
  const [document, setDocument] = useState(null);
  const [masterdataItems, setMasterdataItems] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showNoApprovalDialog, setShowNoApprovalDialog] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      // Fetch form configs
      const formRes = await axios.get(`${API}/form-configs`, { params: { page_size: 100 } });
      setFormConfigs(formRes.data.items);

      // Fetch approval configs
      const approvalRes = await axios.get(`${API}/approval-configs`, { params: { page_size: 100 } });
      setApprovalConfigs(approvalRes.data.items);

      if (isEdit) {
        // Fetch document
        const docRes = await axios.get(`${API}/documents/${id}`);
        setDocument(docRes.data);
        setFormData(docRes.data.data || {});
        setSelectedApprovalConfig(docRes.data.approval_config_id || '');
        
        // Find and set form config
        const config = formRes.data.items.find(f => f.id === docRes.data.form_config_id);
        if (config) {
          setSelectedFormConfig(config);
          await fetchMasterdataForConfig(config);
        }
      } else if (formRes.data.items.length > 0) {
        // Set first form config as default
        const defaultConfig = formRes.data.items[0];
        setSelectedFormConfig(defaultConfig);
        await fetchMasterdataForConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterdataForConfig = async (config) => {
    const items = {};
    for (const field of config.fields) {
      if (field.masterdata_id) {
        try {
          const res = await axios.get(`${API}/masterdata/${field.masterdata_id}/items`, { params: { page_size: 100 } });
          items[field.masterdata_id] = res.data.items;
        } catch (error) {
          console.error(`Failed to fetch masterdata for ${field.name}:`, error);
        }
      }
    }
    setMasterdataItems(items);
  };

  const handleFormConfigChange = async (configId) => {
    const config = formConfigs.find(f => f.id === configId);
    setSelectedFormConfig(config);
    setFormData({});
    if (config) {
      await fetchMasterdataForConfig(config);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error(t('onlyPdf'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('maxFileSize'));
      return;
    }

    setUploadingFile(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      handleFieldChange(fieldName, { 
        id: res.data.id, 
        filename: res.data.filename 
      });
      toast.success('File uploaded');
    } catch (error) {
      toast.error(parseApiError(error));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSave = async (submit = false, forceNoApproval = false) => {
    if (!selectedFormConfig) {
      toast.error('Please select a form type');
      return;
    }

    // Validate required fields
    const missingFields = selectedFormConfig.fields
      .filter(f => f.required && !formData[f.name] && f.name !== 'tidak_ada_tanggal_berakhir')
      .map(f => f.label);

    // Check tanggal_berakhir conditionally
    const noEndDate = formData['tidak_ada_tanggal_berakhir'];
    if (!noEndDate) {
      const endDateField = selectedFormConfig.fields.find(f => f.name === 'tanggal_berakhir');
      if (endDateField?.required && !formData['tanggal_berakhir']) {
        missingFields.push(endDateField.label);
      }
    }

    if (missingFields.length > 0 && submit) {
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Show confirmation if no approval workflow selected when submitting
    if (submit && !selectedApprovalConfig && !forceNoApproval) {
      setShowNoApprovalDialog(true);
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/documents/${id}`, { data: formData });
        if (submit) {
          const submitUrl = selectedApprovalConfig 
            ? `${API}/documents/${id}/submit?approval_config_id=${selectedApprovalConfig}`
            : `${API}/documents/${id}/submit`;
          await axios.post(submitUrl);
          toast.success(selectedApprovalConfig ? 'Document submitted for approval' : 'Document approved directly');
        } else {
          toast.success('Document saved');
        }
      } else {
        const createRes = await axios.post(`${API}/documents`, {
          form_config_id: selectedFormConfig.id,
          data: formData
        });
        
        if (submit) {
          const submitUrl = selectedApprovalConfig 
            ? `${API}/documents/${createRes.data.id}/submit?approval_config_id=${selectedApprovalConfig}`
            : `${API}/documents/${createRes.data.id}/submit`;
          await axios.post(submitUrl);
          toast.success(selectedApprovalConfig ? 'Document created and submitted' : 'Document created and approved');
        } else {
          toast.success('Document created');
        }
      }
      navigate('/documents');
    } catch (error) {
      toast.error(parseApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitWithoutApproval = () => {
    setShowNoApprovalDialog(false);
    handleSave(true, true);
  };

  const renderField = (field) => {
    const value = formData[field.name];

    switch (field.field_type) {
      case 'Text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label}`}
            data-testid={`field-${field.name}`}
          />
        );

      case 'Number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label}`}
            data-testid={`field-${field.name}`}
          />
        );

      case 'Currency':
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              className="pl-10"
              placeholder="0"
              data-testid={`field-${field.name}`}
            />
          </div>
        );

      case 'Dropdown':
        const options = field.masterdata_id 
          ? masterdataItems[field.masterdata_id]?.map(item => item.values?.value) || []
          : field.options || [];
        
        return (
          <Select 
            value={value || ''} 
            onValueChange={(v) => handleFieldChange(field.name, v)}
          >
            <SelectTrigger data-testid={`field-${field.name}`}>
              <SelectValue placeholder={t('selectOption')} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt, idx) => (
                <SelectItem key={idx} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'Date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-full justify-start text-left font-normal ${!value ? 'text-muted-foreground' : ''}`}
                data-testid={`field-${field.name}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleFieldChange(field.name, date?.toISOString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case 'Checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={value || false}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked)}
              data-testid={`field-${field.name}`}
            />
            <label htmlFor={field.name} className="text-sm text-gray-600">
              {field.label}
            </label>
          </div>
        );

      case 'File':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <label className="flex-1">
                <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  uploadingFile ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-primary hover:bg-primary/10'
                }`}>
                  {uploadingFile ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">{t('uploadFile')}</p>
                      <p className="text-xs text-gray-400 mt-1">{t('maxFileSize')} - {t('onlyPdf')}</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => handleFileUpload(field.name, e.target.files[0])}
                  disabled={uploadingFile}
                  data-testid={`field-${field.name}`}
                />
              </label>
            </div>
            {value && (
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm text-gray-700">{value.filename}</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            data-testid={`field-${field.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canEdit = !document || document.status === 'draft' || document.status === 'rejected';

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="document-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/documents')}
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {isEdit ? (canEdit ? 'Edit Document' : 'View Document') : 'New Document'}
          </h1>
          {document && (
            <p className="text-gray-500 mt-1">
              Status: <span className={`font-medium ${
                document.status === 'approved' ? 'text-emerald-600' :
                document.status === 'pending' ? 'text-amber-600' :
                document.status === 'rejected' ? 'text-red-600' : 'text-gray-600'
              }`}>{t(document.status)}</span>
            </p>
          )}
        </div>
      </div>

      {/* Form Type Selection */}
      {!isEdit && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Outfit']">Form Type</CardTitle>
            <CardDescription>Select the type of document you want to create</CardDescription>
          </CardHeader>
          <CardContent>
            <Select 
              value={selectedFormConfig?.id || ''} 
              onValueChange={handleFormConfigChange}
            >
              <SelectTrigger data-testid="form-type-select">
                <SelectValue placeholder="Select form type" />
              </SelectTrigger>
              <SelectContent>
                {formConfigs.map(config => (
                  <SelectItem key={config.id} value={config.id}>{config.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Form Fields */}
      {selectedFormConfig && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Outfit']">
              {selectedFormConfig.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedFormConfig.fields.map((field) => {
                // Skip tanggal_berakhir if checkbox is checked
                if (field.name === 'tanggal_berakhir' && formData['tidak_ada_tanggal_berakhir']) {
                  return null;
                }

                // Full width for certain field types
                const fullWidth = ['File', 'Checkbox'].includes(field.field_type);

                return (
                  <div 
                    key={field.name} 
                    className={`space-y-2 ${fullWidth ? 'md:col-span-2' : ''}`}
                  >
                    {field.field_type !== 'Checkbox' && (
                      <Label className="text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                    )}
                    {canEdit ? renderField(field) : (
                      <div className="p-3 bg-gray-50 rounded-md text-gray-700">
                        {field.field_type === 'Date' && formData[field.name]
                          ? format(new Date(formData[field.name]), 'PPP')
                          : field.field_type === 'Currency'
                          ? `Rp ${Number(formData[field.name] || 0).toLocaleString()}`
                          : field.field_type === 'Checkbox'
                          ? (formData[field.name] ? 'Yes' : 'No')
                          : field.field_type === 'File'
                          ? formData[field.name]?.filename || '-'
                          : formData[field.name] || '-'
                        }
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Config Selection (only for submit) */}
      {canEdit && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Outfit']">Approval Workflow</CardTitle>
            <CardDescription>Select the approval workflow for submission</CardDescription>
          </CardHeader>
          <CardContent>
            <Select 
              value={selectedApprovalConfig} 
              onValueChange={setSelectedApprovalConfig}
            >
              <SelectTrigger data-testid="approval-config-select">
                <SelectValue placeholder="Select approval workflow" />
              </SelectTrigger>
              <SelectContent>
                {approvalConfigs.map(config => (
                  <SelectItem key={config.id} value={config.id}>
                    {config.name} ({config.steps?.length || 0} steps)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Approval History */}
      {document?.approval_history?.length > 0 && (
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Outfit']">{t('history')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {document.approval_history.map((entry, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg border ${
                    entry.action === 'approve' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{entry.user_name}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        entry.action === 'approve' ? 'bg-emerald-200 text-emerald-700' : 'bg-red-200 text-red-700'
                      }`}>
                        {entry.action === 'approve' ? t('approved') : t('rejected')}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  {entry.comment && (
                    <p className="text-sm text-gray-600 mt-2">{entry.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {canEdit && (
        <div className="flex items-center justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/documents')}
            data-testid="cancel-btn"
          >
            {t('cancel')}
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t('save')} as Draft
          </Button>
          <Button 
            onClick={() => handleSave(true)}
            disabled={saving}
            data-testid="submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            {t('submit')}
          </Button>
        </div>
      )}

      {/* No Approval Workflow Confirmation Dialog */}
      <AlertDialog open={showNoApprovalDialog} onOpenChange={setShowNoApprovalDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              No Approval Workflow Selected
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to submit this document without an approval workflow. 
              The document will be directly approved without going through any approval process.
              <br /><br />
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-no-approval">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSubmitWithoutApproval}
              data-testid="confirm-no-approval"
            >
              Yes, Approve Directly
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentFormPage;
